import type { AppQueryApi, QueryHandlerMap } from '@yada/plugin-api';

/** Loads the headless handlers of an app that has not registered any yet. */
export type QueryResolver = (appId: string) => Promise<QueryHandlerMap | null>;

export class QueryRegistry {
  private handlers = new Map<string, QueryHandlerMap>();
  private resolving = new Map<string, Promise<QueryHandlerMap | null>>();
  private resolver: QueryResolver | null = null;

  setResolver(resolver: QueryResolver): void {
    this.resolver = resolver;
  }

  register(appId: string, handlerMap: QueryHandlerMap): void {
    this.handlers.set(appId, handlerMap);
  }

  unregister(appId: string): void {
    this.handlers.delete(appId);
  }

  private async handlersFor(appId: string): Promise<QueryHandlerMap | null> {
    const registered = this.handlers.get(appId);
    if (registered || !this.resolver) return registered ?? null;
    let pending = this.resolving.get(appId);
    if (!pending) {
      pending = this.resolver(appId).catch((err) => {
        console.error(`[QueryRegistry] Could not load query handlers for ${appId}`, err);
        return null;
      });
      this.resolving.set(appId, pending);
    }
    const resolved = await pending;
    // A failed load is retried on the next query; a mounted app's own handlers win.
    if (!resolved) this.resolving.delete(appId);
    else if (!this.handlers.has(appId)) this.handlers.set(appId, resolved);
    return this.handlers.get(appId) ?? null;
  }

  async query<TResult = unknown>(
    appId: string,
    queryName: string,
    params?: unknown
  ): Promise<TResult | null> {
    const appHandlers = await this.handlersFor(appId);
    if (!appHandlers) return null;
    const handler = appHandlers[queryName];
    if (!handler) return null;
    try {
      return (await handler(params)) as TResult;
    } catch (err) {
      console.error(`[QueryRegistry] Query failed: ${appId}.${queryName}`, err);
      return null;
    }
  }

  createApi(appId: string): AppQueryApi {
    return {
      register: (handlers) => this.register(appId, handlers),
      query: (targetAppId, queryName, params) => this.query(targetAppId, queryName, params),
    };
  }
}
